import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { SlidersHorizontal, Check } from "lucide-react";
import { products, collections, subCollections } from "@/data/products";
import ProductCard from "@/components/product/ProductCard";

const SORT_OPTIONS = [
  { value: "featured", label: "Featured" },
  { value: "new", label: "New Arrivals" },
  { value: "price-asc", label: "Price · Low to High" },
  { value: "price-desc", label: "Price · High to Low" },
];

export default function Shop() {
  const { collection } = useParams();
  const [activeCategory, setActiveCategory] = useState(collection || "all");
  const [activeSub, setActiveSub] = useState("all");
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    // Route param maps to category or subcollection
    if (!collection) {
      setActiveCategory("all");
      setActiveSub("all");
      return;
    }
    if (collections.find((c) => c.slug === collection)) {
      setActiveCategory(collection);
      setActiveSub("all");
    } else if (subCollections.find((s) => s.slug === collection)) {
      setActiveCategory("all");
      setActiveSub(collection);
    } else {
      setActiveCategory("all");
      setActiveSub("all");
    }
  }, [collection]);

  const filtered = useMemo(() => {
    let list = [...products];
    if (activeCategory !== "all") {
      const c = collections.find((x) => x.slug === activeCategory);
      if (c) list = list.filter(c.filter);
    }
    if (activeSub !== "all") {
      list = list.filter(
        (p) => p.collection.toLowerCase() === activeSub.toLowerCase()
      );
    }
    switch (sort) {
      case "new":
        list.sort((a, b) => (b.isNew === true) - (a.isNew === true));
        break;
      case "price-asc":
        list.sort((a, b) => a.price - b.price);
        break;
      case "price-desc":
        list.sort((a, b) => b.price - a.price);
        break;
      default:
        break;
    }
    return list;
  }, [activeCategory, activeSub, sort]);

  // Sub-collections available inside the active category
  const availableSubs = useMemo(() => {
    let scope = products;
    if (activeCategory !== "all") {
      const c = collections.find((x) => x.slug === activeCategory);
      if (c) scope = scope.filter(c.filter);
    }
    const setSlugs = new Set(scope.map((p) => p.collection.toLowerCase()));
    return subCollections.filter((s) => setSlugs.has(s.slug));
  }, [activeCategory]);

  const activeCat = collections.find((c) => c.slug === activeCategory);
  const activeSubObj = subCollections.find((s) => s.slug === activeSub);

  const title = activeSubObj?.name || activeCat?.name || "The Collection";
  const tagline =
    activeSubObj?.tagline ||
    activeCat?.tagline ||
    "Handcrafted earrings & pendants shaped from semi-precious crystals and freshwater pearls.";
  const bandCover =
    activeCat?.cover ||
    (activeSubObj &&
      products.find((p) => p.collection.toLowerCase() === activeSubObj.slug)?.images.lifestyle) ||
    products.find((p) => p.slug === "forest-whisper-earrings").images.lifestyle;

  return (
    <div data-testid="shop-page" className="bg-[#FAF8F5]">
      {/* Header band */}
      <section className="relative border-b border-[#DCD5CA] bg-[#F0EBE1]/60 overflow-hidden">
        {bandCover && (
          <img src={bandCover} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-[#FAF8F5] via-[#FAF8F5]/70 to-transparent" />
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16 py-14 md:py-24">
          <p className="overline">
            {activeCategory === "all" && activeSub === "all"
              ? "Every piece"
              : activeCategory !== "all"
              ? "Category"
              : "Collection"}
          </p>
          <div className="mt-3 md:mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-5 md:gap-6">
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight text-[#2A2626] leading-none">
              {title}
            </h1>
            <p className="text-sm text-[#5C564F] max-w-md">{tagline}</p>
          </div>
        </div>
      </section>

      {/* Primary category tabs — the ONLY two product lines */}
      <section className="border-b border-[#DCD5CA] bg-[#FAF8F5]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16">
          <nav className="flex items-center justify-center gap-2 md:gap-3 py-4 md:py-5">
            <Link
              to="/shop"
              data-testid="filter-all"
              className={`px-5 md:px-6 py-2.5 text-[11px] uppercase tracking-[0.24em] transition-colors ${
                activeCategory === "all" && activeSub === "all"
                  ? "bg-[#2A2626] text-[#FAF8F5]"
                  : "bg-transparent text-[#2A2626] hover:bg-[#F0EBE1]"
              }`}
            >
              All
            </Link>
            {collections.map((c) => (
              <Link
                key={c.slug}
                to={`/shop/${c.slug}`}
                data-testid={`filter-${c.slug}`}
                className={`px-5 md:px-6 py-2.5 text-[11px] uppercase tracking-[0.24em] transition-colors ${
                  activeCategory === c.slug
                    ? "bg-[#2A2626] text-[#FAF8F5]"
                    : "bg-transparent text-[#2A2626] hover:bg-[#F0EBE1]"
                }`}
              >
                {c.name}
              </Link>
            ))}
          </nav>
        </div>
      </section>

      {/* Sub-collection chips + Sort */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16 pt-6 md:pt-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6">
          {availableSubs.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 overflow-x-auto pb-1">
              <span className="overline mr-1 shrink-0">Style:</span>
              <button
                type="button"
                onClick={() => setActiveSub("all")}
                data-testid="sub-filter-all"
                className={`shrink-0 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] border transition-colors ${
                  activeSub === "all"
                    ? "border-[#2A2626] text-[#2A2626] bg-[#F0EBE1]"
                    : "border-[#DCD5CA] text-[#5C564F] hover:border-[#2A2626] hover:text-[#2A2626]"
                }`}
              >
                All styles
              </button>
              {availableSubs.map((s) => (
                <button
                  key={s.slug}
                  type="button"
                  onClick={() => setActiveSub(activeSub === s.slug ? "all" : s.slug)}
                  data-testid={`sub-filter-${s.slug}`}
                  className={`shrink-0 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] border transition-colors ${
                    activeSub === s.slug
                      ? "border-[#2A2626] text-[#2A2626] bg-[#F0EBE1]"
                      : "border-[#DCD5CA] text-[#5C564F] hover:border-[#2A2626] hover:text-[#2A2626]"
                  }`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center gap-4 md:ml-auto">
            <span className="text-[11px] uppercase tracking-[0.22em] text-[#5C564F] hidden md:inline">
              {filtered.length} pieces
            </span>
            <div className="relative">
              <SlidersHorizontal
                size={15}
                strokeWidth={1.5}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C564F] pointer-events-none"
              />
              <select
                data-testid="sort-select"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="appearance-none bg-transparent border border-[#DCD5CA] pl-9 pr-8 py-2 text-[11px] uppercase tracking-[0.22em] text-[#2A2626] focus:outline-none focus:border-[#2A2626] cursor-pointer"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16 py-10 md:py-16">
        {filtered.length === 0 ? (
          <div data-testid="shop-empty-state" className="text-center py-32">
            <p className="font-serif text-3xl text-[#2A2626]">Nothing here yet.</p>
            <p className="mt-3 text-sm text-[#5C564F]">Try browsing another style.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-10 md:gap-x-8 md:gap-y-16">
            {filtered.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </section>

      <section className="border-t border-[#DCD5CA] bg-[#F0EBE1]/60">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16 py-10 md:py-12 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {[
            "Complimentary shipping over ₹4,000",
            "925 Sterling Silver ear hooks",
            "Secure online payments via PhonePe",
          ].map((t) => (
            <div key={t} className="flex items-start gap-3">
              <Check size={16} strokeWidth={1.5} className="text-[#2A2626] mt-1" />
              <p className="text-sm text-[#2A2626]">{t}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
